import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Loader2, Instagram, X } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const GENRE_OPTIONS = [
  "Literary Fiction", "Sci-Fi", "Fantasy", "Romance", "Mystery",
  "Thriller", "Horror", "Historical Fiction", "Poetry", "Memoir",
  "Self-Help", "Young Adult", "Graphic Novels", "Non-Fiction", "LGBTQ+",
  "Indie Lit", "Afrofuturism", "Magical Realism", "Cli-Fi", "Dystopian",
];

const schema = z.object({
  name: z.string().trim().min(1, "Name is required").max(200),
  email: z.string().trim().email("Please enter a valid email").max(255),
  platform: z.enum(["instagram", "tiktok", "both"], { required_error: "Please select a platform" }),
  handle: z.string().trim().min(1, "Handle is required").max(100),
  profileUrl: z.string().trim().url("Please enter a valid URL").max(500).or(z.literal("")).optional(),
  followerCount: z.string().trim().max(50).optional(),
  genres: z.array(z.string()).min(1, "Select at least one genre"),
  bio: z.string().trim().min(20, "Tell us more about your content (at least 20 characters)").max(2000),
});

type FormData = z.infer<typeof schema>;

export default function PartnerInfluencer() {
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hp, setHp] = useState(""); // honeypot field
  const [form, setForm] = useState<FormData>({
    name: "",
    email: "",
    platform: "instagram",
    handle: "",
    profileUrl: "",
    followerCount: "",
    genres: [],
    bio: "",
  });

  const update = (field: keyof FormData, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const toggleGenre = (genre: string) => {
    setForm((prev) => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter((g) => g !== genre)
        : [...prev.genres, genre],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Honeypot check — bots fill hidden fields
    if (hp) { setSubmitted(true); return; }

    const result = schema.safeParse(form);
    if (!result.success) {
      toast.error(result.error.errors[0]?.message || "Please check your input");
      return;
    }

    setSaving(true);
    const { error } = await supabase.from("influencer_submissions" as any).insert({
      name: result.data.name,
      email: result.data.email,
      platform: result.data.platform,
      handle: result.data.handle,
      profile_url: result.data.profileUrl || null,
      follower_count: result.data.followerCount || null,
      genres: result.data.genres,
      bio: result.data.bio,
    } as any);
    setSaving(false);

    if (error) {
      toast.error("Failed to submit. Please try again.");
      return;
    }

    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <div className="max-w-md text-center">
          <CheckCircle2 className="mx-auto mb-4 text-primary" size={48} />
          <h1 className="mb-2 font-display text-3xl font-extrabold text-foreground">
            You're In!
          </h1>
          <p className="mb-6 text-muted-foreground">
            Thanks for applying to partner with Shhh... Book Club. We'll review your profile and
            reach out soon. Once approved, your account will be recommended to readers who match your vibe.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setForm({ name: "", email: "", platform: "instagram", handle: "", profileUrl: "", followerCount: "", genres: [], bio: "" });
            }}
            variant="outline"
            className="rounded-xl"
          >
            Submit Another Application
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-2xl px-4 py-12">
        {/* Hero blurb */}
        <div className="mb-10 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary/10">
            <Instagram className="h-8 w-8 text-secondary" />
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
            Influencer Partnership
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
            Are you a Bookstagrammer or BookTok creator? Partner with us and we'll connect your page
            directly to readers who share your taste. When someone asks for a recommendation in your
            genre, we'll point them to <em>you</em>.
          </p>
        </div>

        {/* How it works */}
        <div className="mb-10 rounded-2xl border border-border/50 bg-card p-6">
          <h2 className="mb-3 font-display text-lg font-bold text-foreground">How it works</h2>
          <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
            <li>Submit your profile below with the genres you cover.</li>
            <li>We tag your account with genre & vibe keywords in our system.</li>
            <li>When a reader asks for recs in your wheelhouse, we surface your page alongside our picks.</li>
            <li>You grow your audience with readers who <em>already</em> love what you love.</li>
          </ol>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="inf-name">Your Name *</Label>
              <Input id="inf-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Doe" className="rounded-xl" maxLength={200} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inf-email">Email *</Label>
              <Input id="inf-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" className="rounded-xl" maxLength={255} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Platform *</Label>
            <div className="flex flex-wrap gap-2">
              {(["instagram", "tiktok", "both"] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => update("platform", p)}
                  className={`rounded-xl border px-4 py-2 text-sm font-semibold transition-colors ${
                    form.platform === p
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {p === "both" ? "Both" : p === "instagram" ? "Instagram" : "TikTok"}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="inf-handle">Handle *</Label>
              <Input id="inf-handle" value={form.handle} onChange={(e) => update("handle", e.target.value)} placeholder="@yourbookishhandle" className="rounded-xl" maxLength={100} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inf-followers">Approx. Followers</Label>
              <Input id="inf-followers" value={form.followerCount} onChange={(e) => update("followerCount", e.target.value)} placeholder="e.g. 5k, 12k" className="rounded-xl" maxLength={50} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="inf-url">Profile URL</Label>
            <Input id="inf-url" value={form.profileUrl} onChange={(e) => update("profileUrl", e.target.value)} placeholder="https://instagram.com/yourbookishhandle" className="rounded-xl" maxLength={500} />
          </div>

          {/* Genre tags */}
          <div className="space-y-3">
            <Label>Genres You Cover *</Label>
            <p className="text-xs text-muted-foreground">Select all that apply. These help us match you with the right readers.</p>
            <div className="flex flex-wrap gap-2">
              {GENRE_OPTIONS.map((genre) => (
                <Badge
                  key={genre}
                  variant={form.genres.includes(genre) ? "default" : "outline"}
                  className={`cursor-pointer rounded-full px-3 py-1.5 text-sm transition-colors ${
                    form.genres.includes(genre)
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "hover:bg-muted"
                  }`}
                  onClick={() => toggleGenre(genre)}
                >
                  {genre}
                  {form.genres.includes(genre) && <X className="ml-1 h-3 w-3" />}
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="inf-bio">About Your Content *</Label>
            <Textarea
              id="inf-bio"
              value={form.bio}
              onChange={(e) => update("bio", e.target.value)}
              placeholder="Tell us about your bookish content — your style, what makes your recs unique, and what readers can expect from your page."
              className="min-h-[120px] rounded-xl"
              maxLength={2000}
            />
          </div>

          {/* Honeypot — hidden from real users */}
          <div aria-hidden="true" className="absolute left-[-9999px]">
            <label htmlFor="inf_website_confirm">Leave blank</label>
            <input id="inf_website_confirm" name="inf_website_confirm" type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
          </div>

          <Button type="submit" size="lg" className="w-full rounded-xl font-semibold" disabled={saving}>
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</> : "Apply to Partner"}
          </Button>
        </form>
      </div>
    </div>
  );
}
